import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { menuApi, eventsApi, imagesApi, contentApi, contactApi, authApi } from '@/lib/api';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { LOGO_URL } from '@/lib/constants';
import {
  LayoutDashboard, Image, UtensilsCrossed, CalendarDays, FileText, Phone,
  ArrowLeft, Plus, Pencil, Trash2, Upload, Search, Save, Clock, MapPin, Mail, Globe,
  Check, ExternalLink, Loader2, ChevronRight, LogOut, GripVertical, X,
  ChevronUp, ChevronDown, Shield,
} from 'lucide-react';

/* ── Toast ── */
function useToast() {
  const [toasts, setToasts] = useState([]);
  const show = useCallback((msg, type = 'success') => {
    const id = Date.now();
    setToasts((t) => [...t, { id, msg, type }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 2800);
  }, []);
  return { toasts, show };
}
function ToastContainer({ toasts }) {
  return (
    <div className="fixed bottom-6 right-6 z-[100] space-y-2">
      {toasts.map((t) => (
        <div key={t.id} className={`animate-slide-down px-5 py-3 rounded-xl font-body text-sm font-semibold flex items-center gap-2 shadow-xl ${t.type === 'error' ? 'bg-red-500/90 text-white' : 'bg-emerald-500/90 text-[#141413]'}`}>
          <Check size={16} /> {t.msg}
        </div>
      ))}
    </div>
  );
}

const TABS = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'menu', label: 'Menü', icon: UtensilsCrossed },
  { id: 'events', label: 'Events', icon: CalendarDays },
  { id: 'bilder', label: 'Bilder', icon: Image },
  { id: 'inhalte', label: 'Inhalte', icon: FileText },
  { id: 'kontakt', label: 'Kontakt', icon: Phone },
  { id: 'sicherheit', label: 'Sicherheit', icon: Shield },
];

/* ════════════ DASHBOARD ════════════ */
function DashboardTab({ setTab, stats }) {
  return (
    <div>
      <h3 className="text-stone-100 font-display font-bold text-lg tracking-wide uppercase mb-5">Übersicht</h3>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {stats.map((s) => (
          <div key={s.label} className="glass-card rounded-xl p-5">
            <p className="text-stone-500 text-[10px] font-body uppercase tracking-wider">{s.label}</p>
            <p className="text-stone-100 text-3xl font-display font-bold mt-1">{s.value}</p>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[{ l: 'Menü bearbeiten', i: UtensilsCrossed, t: 'menu' }, { l: 'Event erstellen', i: CalendarDays, t: 'events' }, { l: 'Bilder verwalten', i: Image, t: 'bilder' }, { l: 'Kontakt ändern', i: Phone, t: 'kontakt' }].map((q) => (
          <button key={q.l} onClick={() => setTab(q.t)} className="glass-card rounded-lg p-4 text-left hover:border-amber-400/20 transition-all group">
            <q.i size={18} className="text-amber-400 mb-2" /><p className="text-stone-300 text-xs font-body font-medium">{q.l}</p>
          </button>
        ))}
      </div>
    </div>
  );
}

/* ════════════ MENU CRUD ════════════ */
function MenuTab({ toast }) {
  const [cats, setCats] = useState([]);
  const [selCat, setSelCat] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [newPrice, setNewPrice] = useState('');
  const [newSize, setNewSize] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState({});

  const loadCats = useCallback(async () => {
    try {
      const res = await menuApi.getCategories();
      setCats(res.data.categories);
      if (!selCat && res.data.categories.length > 0) setSelCat(res.data.categories[0].id);
    } catch (e) {
      toast('Menü-Kategorien konnten nicht geladen werden', 'error');
    } finally {
      setLoading(false);
    }
  }, [selCat, toast]);

  const loadItems = useCallback(async () => {
    if (!selCat) return;
    try {
      const res = await menuApi.getItems(selCat);
      setItems(res.data.items);
    } catch (e) {
      toast('Menü-Positionen konnten nicht geladen werden', 'error');
    }
  }, [selCat, toast]);

  useEffect(() => { loadCats(); }, [loadCats]);
  useEffect(() => { loadItems(); }, [loadItems]);

  const addItem = async () => {
    if (!newName.trim()) return;
    await menuApi.createItem({
      category_id: selCat,
      name: newName,
      price: newPrice,
      size_label: newSize,
      description: newDesc,
      order: items.length,
    });
    toast('Position hinzugefügt');
    setNewName(''); setNewPrice(''); setNewSize(''); setNewDesc('');
    loadItems();
  };

  const deleteItem = async (id, name) => {
    await menuApi.deleteItem(id);
    toast(`${name} entfernt`);
    loadItems();
  };

  const startEdit = (item) => {
    setEditId(item.id);
    setEditData({
      name: item.name,
      price: item.price || '',
      size_label: item.size_label || item.detail || '',
      description: item.description || '',
    });
  };
  const saveEdit = async () => {
    const item = items.find(i => i.id === editId);
    // Keep detail in sync with size_label for backward compat of legacy rendering
    await menuApi.updateItem(editId, { ...item, ...editData, detail: editData.size_label });
    toast('Änderung gespeichert');
    setEditId(null);
    loadItems();
  };

  const toggleItem = async (item) => {
    await menuApi.updateItem(item.id, { ...item, active: !item.active });
    toast(item.active ? 'Deaktiviert' : 'Aktiviert');
    loadItems();
  };

  const moveItem = async (idx, dir) => {
    const newItems = [...items];
    const swapIdx = idx + dir;
    if (swapIdx < 0 || swapIdx >= newItems.length) return;
    [newItems[idx], newItems[swapIdx]] = [newItems[swapIdx], newItems[idx]];
    setItems(newItems);
    await menuApi.reorderItems(newItems.map(i => i.id));
    toast('Reihenfolge aktualisiert');
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="text-amber-400 animate-spin" size={32} /></div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h3 className="text-stone-100 font-display font-bold text-lg tracking-wide uppercase">Menü verwalten</h3>
      </div>

      {/* Category pills — horizontally scrollable on mobile */}
      <div className="flex gap-2 mb-5 overflow-x-auto pb-2 -mx-4 px-4 lg:mx-0 lg:px-0 lg:flex-wrap scrollbar-hide">
        {cats.map((c) => (
          <button key={c.id} onClick={() => setSelCat(c.id)} className={`shrink-0 px-4 py-2 rounded-full text-xs font-body font-medium transition-all ${selCat === c.id ? 'bg-amber-500 text-[#141413]' : 'glass-card text-stone-400 hover:text-stone-200'}`}>
            {c.name} <span className={`text-[10px] ml-1 ${selCat === c.id ? 'text-[#141413]/60' : 'text-stone-600'}`}>{c.item_count}</span>
          </button>
        ))}
      </div>

      {/* ────── DESKTOP: Table view (lg+) ────── */}
      <div className="hidden lg:block glass-card rounded-xl overflow-hidden mb-5">
        <table className="w-full">
          <thead>
            <tr className="border-b border-white/5">
              <th className="w-16 p-3 text-stone-500 text-[10px] font-body uppercase tracking-wider text-center">Pos.</th>
              <th className="text-left p-3 text-stone-500 text-[10px] font-body uppercase tracking-wider">Name</th>
              <th className="text-left p-3 text-stone-500 text-[10px] font-body uppercase tracking-wider w-24">Grösse</th>
              <th className="text-left p-3 text-stone-500 text-[10px] font-body uppercase tracking-wider">Beschreibung</th>
              <th className="text-left p-3 text-stone-500 text-[10px] font-body uppercase tracking-wider w-24">Preis</th>
              <th className="text-center p-3 text-stone-500 text-[10px] font-body uppercase tracking-wider">Aktiv</th>
              <th className="text-right p-3 text-stone-500 text-[10px] font-body uppercase tracking-wider">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, idx) => (
              <tr key={item.id} className="border-b border-white/5 hover:bg-white/[0.02] align-top">
                <td className="p-3 text-center">
                  <div className="flex flex-col items-center gap-0.5">
                    <button onClick={() => moveItem(idx, -1)} className="text-stone-600 hover:text-amber-400 transition-colors disabled:opacity-20" disabled={idx === 0}><ChevronUp size={14} /></button>
                    <button onClick={() => moveItem(idx, 1)} className="text-stone-600 hover:text-amber-400 transition-colors disabled:opacity-20" disabled={idx === items.length - 1}><ChevronDown size={14} /></button>
                  </div>
                </td>
                <td className="p-3">
                  {editId === item.id ? (
                    <Input value={editData.name} onChange={(e) => setEditData(d => ({...d, name: e.target.value}))} className="bg-[#141413] border-amber-400/30 text-stone-200 text-sm h-8" />
                  ) : (
                    <span className="text-stone-200 text-sm font-body font-medium">{item.name}</span>
                  )}
                </td>
                <td className="p-3">
                  {editId === item.id ? (
                    <Input value={editData.size_label} onChange={(e) => setEditData(d => ({...d, size_label: e.target.value}))} placeholder="9cl, 3dl..." className="bg-[#141413] border-amber-400/30 text-stone-200 text-sm h-8" />
                  ) : (
                    <span className="text-stone-400 text-sm font-body">{item.size_label || item.detail || (item.sizes?.length > 0 ? item.sizes.map(s => s.label).join('/') : '–')}</span>
                  )}
                </td>
                <td className="p-3">
                  {editId === item.id ? (
                    <textarea value={editData.description} onChange={(e) => setEditData(d => ({...d, description: e.target.value}))} placeholder="Zutaten, z. B. Gin, Campari, roter Vermouth" className="w-full h-14 px-2 py-1.5 bg-[#141413] border border-amber-400/30 rounded text-stone-200 text-xs font-body resize-none focus:outline-none focus:ring-1 focus:ring-amber-400/50" />
                  ) : (
                    <span className="text-stone-500 text-xs font-body italic line-clamp-2">{item.description || '–'}</span>
                  )}
                </td>
                <td className="p-3">
                  {editId === item.id ? (
                    <Input value={editData.price} onChange={(e) => setEditData(d => ({...d, price: e.target.value}))} className="bg-[#141413] border-amber-400/30 text-stone-200 text-sm h-8 w-24" />
                  ) : (
                    <span className="text-amber-400 text-sm font-body font-semibold">{item.price || (item.sizes?.length > 0 ? item.sizes.map(s => s.price).filter(Boolean).join(' / ') : '–')}</span>
                  )}
                </td>
                <td className="p-3 text-center"><Switch checked={item.active} onCheckedChange={() => toggleItem(item)} /></td>
                <td className="p-3">
                  <div className="flex items-center justify-end gap-1">
                    {editId === item.id ? (
                      <>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-emerald-400" onClick={saveEdit} data-testid="menu-item-save"><Check size={13} /></Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-stone-500" onClick={() => setEditId(null)}><X size={13} /></Button>
                      </>
                    ) : (
                      <>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-stone-500 hover:text-amber-400" onClick={() => startEdit(item)} data-testid={`menu-item-edit-${item.id}`}><Pencil size={12} /></Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-stone-500 hover:text-red-400" onClick={() => deleteItem(item.id, item.name)}><Trash2 size={12} /></Button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ────── MOBILE/TABLET: Card view (<lg) ────── */}
      <div className="lg:hidden space-y-3 mb-5">
        {items.map((item, idx) => (
          <div key={item.id} className="glass-card rounded-xl p-4" data-testid={`menu-item-card-${item.id}`}>
            {editId === item.id ? (
              <div className="space-y-3">
                <div>
                  <label className="text-stone-400 text-[10px] font-body uppercase tracking-wider mb-1 block">Name</label>
                  <Input value={editData.name} onChange={(e) => setEditData(d => ({...d, name: e.target.value}))} className="bg-[#141413] border-amber-400/30 text-stone-200 text-sm" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-stone-400 text-[10px] font-body uppercase tracking-wider mb-1 block">Grösse</label>
                    <Input value={editData.size_label} onChange={(e) => setEditData(d => ({...d, size_label: e.target.value}))} placeholder="9cl" className="bg-[#141413] border-amber-400/30 text-stone-200 text-sm" />
                  </div>
                  <div>
                    <label className="text-stone-400 text-[10px] font-body uppercase tracking-wider mb-1 block">Preis</label>
                    <Input value={editData.price} onChange={(e) => setEditData(d => ({...d, price: e.target.value}))} placeholder="CHF" className="bg-[#141413] border-amber-400/30 text-stone-200 text-sm" />
                  </div>
                </div>
                <div>
                  <label className="text-stone-400 text-[10px] font-body uppercase tracking-wider mb-1 block">Beschreibung</label>
                  <textarea value={editData.description} onChange={(e) => setEditData(d => ({...d, description: e.target.value}))} placeholder="Zutaten, z. B. Gin, Campari, roter Vermouth" className="w-full h-16 px-3 py-2 bg-[#141413] border border-amber-400/30 rounded-lg text-stone-200 text-sm font-body resize-none focus:outline-none focus:ring-1 focus:ring-amber-400/50" />
                </div>
                <div className="flex gap-2 pt-1">
                  <Button className="flex-1 bg-emerald-500 hover:bg-emerald-400 text-[#141413] text-xs h-10" onClick={saveEdit} data-testid="menu-item-save-mobile"><Check size={14} className="mr-1.5" /> Speichern</Button>
                  <Button variant="ghost" className="flex-1 text-stone-400 text-xs h-10 border border-white/10" onClick={() => setEditId(null)}><X size={14} className="mr-1.5" /> Abbrechen</Button>
                </div>
              </div>
            ) : (
              <div>
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <h4 className="text-stone-100 font-body font-semibold text-sm">{item.name}</h4>
                      {(item.size_label || item.detail) && <span className="text-stone-500 text-xs font-body">{item.size_label || item.detail}</span>}
                    </div>
                    {item.description && <p className="text-stone-500 text-xs font-body italic mt-1 leading-snug">{item.description}</p>}
                  </div>
                  <span className="text-amber-400 font-body font-bold text-base shrink-0">{item.price || (item.sizes?.length > 0 ? item.sizes.map(s => s.price).filter(Boolean).join(' / ') : '–')}</span>
                </div>
                <div className="flex items-center justify-between pt-3 border-t border-white/5">
                  <div className="flex items-center gap-2">
                    <Switch checked={item.active} onCheckedChange={() => toggleItem(item)} />
                    <span className="text-stone-500 text-xs font-body">{item.active ? 'Aktiv' : 'Inaktiv'}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => moveItem(idx, -1)} disabled={idx === 0} className="w-9 h-9 rounded-lg flex items-center justify-center text-stone-500 hover:text-amber-400 hover:bg-white/5 disabled:opacity-20 transition" aria-label="Nach oben"><ChevronUp size={16} /></button>
                    <button onClick={() => moveItem(idx, 1)} disabled={idx === items.length - 1} className="w-9 h-9 rounded-lg flex items-center justify-center text-stone-500 hover:text-amber-400 hover:bg-white/5 disabled:opacity-20 transition" aria-label="Nach unten"><ChevronDown size={16} /></button>
                    <button onClick={() => startEdit(item)} className="w-9 h-9 rounded-lg flex items-center justify-center text-stone-400 hover:text-amber-400 hover:bg-white/5 transition" aria-label="Bearbeiten" data-testid={`menu-item-edit-mobile-${item.id}`}><Pencil size={15} /></button>
                    <button onClick={() => deleteItem(item.id, item.name)} className="w-9 h-9 rounded-lg flex items-center justify-center text-stone-400 hover:text-red-400 hover:bg-white/5 transition" aria-label="Löschen"><Trash2 size={15} /></button>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="glass-card rounded-xl p-5">
        <h4 className="text-stone-100 font-body font-semibold text-sm mb-3">Schnell hinzufügen</h4>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Name *" className="bg-[#141413] border-white/10 text-stone-200 text-sm" data-testid="menu-new-name" />
          <Input value={newSize} onChange={(e) => setNewSize(e.target.value)} placeholder="Grösse (z. B. 9cl)" className="bg-[#141413] border-white/10 text-stone-200 text-sm" data-testid="menu-new-size" />
          <Input value={newPrice} onChange={(e) => setNewPrice(e.target.value)} placeholder="Preis (CHF)" className="bg-[#141413] border-white/10 text-stone-200 text-sm" data-testid="menu-new-price" />
        </div>
        <textarea
          value={newDesc}
          onChange={(e) => setNewDesc(e.target.value)}
          placeholder="Beschreibung / Zutaten, z. B. Gin, Campari, roter Vermouth"
          className="w-full h-16 mt-3 px-3 py-2 bg-[#141413] border border-white/10 rounded-lg text-stone-200 text-sm font-body resize-none focus:outline-none focus:ring-1 focus:ring-amber-400/50"
          data-testid="menu-new-description"
        />
        <div className="mt-3 flex justify-end">
          <Button className="w-full sm:w-auto bg-amber-500 hover:bg-amber-400 text-[#141413] text-xs h-10" onClick={addItem} data-testid="menu-new-add"><Plus size={14} className="mr-1.5" /> Hinzufügen</Button>
        </div>
      </div>
    </div>
  );
}

/* ════════════ EVENTS CRUD ════════════ */
// Inline image picker: lets user upload a local image, shows preview, allows change/remove.
function EventImagePicker({ value, onChange, label = 'Event-Bild' }) {
  const [uploading, setUploading] = useState(false);
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return alert('Datei zu gross. Maximum: 5 MB');
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await api.post('/upload', form);
      const base = process.env.REACT_APP_BACKEND_URL || '';
      const url = res.data.url.startsWith('http') ? res.data.url : `${base}${res.data.url}`;
      onChange(url);
    } catch (err) {
      alert('Upload fehlgeschlagen');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  return (
    <div className="w-full">
      <label className="text-stone-400 text-[10px] font-body uppercase tracking-wider mb-1.5 block">{label}</label>
      {value ? (
        <div className="flex items-center gap-3 p-2 bg-[#141413] border border-white/10 rounded-lg">
          <img src={value} alt="" className="w-16 h-12 rounded object-cover shrink-0" />
          <div className="flex-1 min-w-0 flex items-center gap-1.5 flex-wrap">
            <label className="cursor-pointer px-2.5 py-1.5 bg-amber-500/10 hover:bg-amber-500/20 border border-amber-400/20 rounded text-amber-400 text-xs font-body font-medium inline-flex items-center gap-1.5">
              {uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
              Foto ändern
              <input type="file" accept="image/*" className="hidden" onChange={handleFile} disabled={uploading} data-testid="event-image-replace" />
            </label>
            <button type="button" onClick={() => onChange('')} className="px-2.5 py-1.5 text-stone-500 hover:text-red-400 text-xs font-body inline-flex items-center gap-1.5">
              <Trash2 size={12} /> Entfernen
            </button>
          </div>
        </div>
      ) : (
        <label className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-amber-500/10 hover:bg-amber-500/20 border border-dashed border-amber-400/30 hover:border-amber-400/50 rounded-lg cursor-pointer transition-all active:scale-[0.99]">
          {uploading ? <Loader2 size={15} className="animate-spin text-amber-400" /> : <Upload size={15} className="text-amber-400" />}
          <span className="text-amber-400 text-sm font-body font-medium">{uploading ? 'Wird hochgeladen...' : 'Foto hochladen'}</span>
          <input type="file" accept="image/*" className="hidden" onChange={handleFile} disabled={uploading} data-testid="event-image-upload" />
        </label>
      )}
    </div>
  );
}

function EventsTab({ toast }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ title: '', description: '', event_type: '', date: '', time: '', image_url: '', published: false });
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState({});

  const load = useCallback(async () => {
    try {
      const res = await eventsApi.getAll();
      setEvents(res.data.events);
    } catch (e) {
      toast('Events konnten nicht geladen werden', 'error');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.title.trim()) return toast('Bitte Titel eingeben', 'error');
    await eventsApi.create({ ...form, order: events.length });
    toast('Event erstellt');
    setForm({ title: '', description: '', event_type: '', date: '', time: '', image_url: '', published: false });
    load();
  };

  const toggle = async (ev) => {
    await eventsApi.update(ev.id, { ...ev, published: !ev.published });
    toast(ev.published ? 'Event deaktiviert' : 'Event veröffentlicht');
    load();
  };

  const remove = async (ev) => {
    await eventsApi.delete(ev.id);
    toast(`"${ev.title}" gelöscht`);
    load();
  };

  const startEdit = (ev) => {
    setEditId(ev.id);
    setEditData({
      title: ev.title, description: ev.description || '', event_type: ev.event_type || '',
      date: ev.date || '', time: ev.time || '', image_url: ev.image_url || '', published: !!ev.published
    });
  };
  const saveEdit = async () => {
    const ev = events.find(e => e.id === editId);
    await eventsApi.update(editId, { ...ev, ...editData });
    toast('Event gespeichert');
    setEditId(null);
    load();
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="text-amber-400 animate-spin" size={32} /></div>;

  return (
    <div>
      <h3 className="text-stone-100 font-display font-bold text-lg tracking-wide uppercase mb-5">Events verwalten</h3>

      <div className="glass-card rounded-xl p-6 mb-8">
        <h4 className="text-stone-100 font-body font-semibold text-sm mb-4">Neues Event</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input value={form.title} onChange={(e) => setForm(f => ({...f, title: e.target.value}))} placeholder="Titel *" className="bg-[#141413] border-white/10 text-stone-200 text-sm" data-testid="event-new-title" />
          <Input value={form.event_type} onChange={(e) => setForm(f => ({...f, event_type: e.target.value}))} placeholder="Typ (Sport, Privat...)" className="bg-[#141413] border-white/10 text-stone-200 text-sm" />
          <Input value={form.date} onChange={(e) => setForm(f => ({...f, date: e.target.value}))} placeholder="Datum" className="bg-[#141413] border-white/10 text-stone-200 text-sm" />
          <Input value={form.time} onChange={(e) => setForm(f => ({...f, time: e.target.value}))} placeholder="Uhrzeit" className="bg-[#141413] border-white/10 text-stone-200 text-sm" />
        </div>
        <div className="mt-4">
          <EventImagePicker value={form.image_url} onChange={(url) => setForm(f => ({...f, image_url: url}))} />
        </div>
        <textarea value={form.description} onChange={(e) => setForm(f => ({...f, description: e.target.value}))} placeholder="Beschreibung..." className="w-full h-16 mt-4 px-3 py-2 bg-[#141413] border border-white/10 rounded-lg text-stone-200 text-sm font-body resize-none focus:outline-none focus:ring-1 focus:ring-amber-400/50" />
        <div className="mt-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Switch checked={form.published} onCheckedChange={(v) => setForm(f => ({...f, published: v}))} />
            <span className="text-stone-400 text-sm font-body">Veröffentlichen</span>
          </div>
          <Button className="bg-amber-500 hover:bg-amber-400 text-[#141413] text-xs" onClick={create} data-testid="event-create"><Plus size={14} className="mr-1.5" /> Event erstellen</Button>
        </div>
      </div>

      <div className="space-y-3">
        {events.map((ev) => (
          editId === ev.id ? (
            <div key={ev.id} className="glass-card rounded-xl p-6 border-amber-400/30">
              <h4 className="text-amber-400 font-body font-semibold text-xs mb-4 uppercase tracking-wider">Event bearbeiten</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input value={editData.title} onChange={(e) => setEditData(d => ({...d, title: e.target.value}))} placeholder="Titel" className="bg-[#141413] border-amber-400/30 text-stone-200 text-sm" />
                <Input value={editData.event_type} onChange={(e) => setEditData(d => ({...d, event_type: e.target.value}))} placeholder="Typ" className="bg-[#141413] border-amber-400/30 text-stone-200 text-sm" />
                <Input value={editData.date} onChange={(e) => setEditData(d => ({...d, date: e.target.value}))} placeholder="Datum" className="bg-[#141413] border-amber-400/30 text-stone-200 text-sm" />
                <Input value={editData.time} onChange={(e) => setEditData(d => ({...d, time: e.target.value}))} placeholder="Uhrzeit" className="bg-[#141413] border-amber-400/30 text-stone-200 text-sm" />
              </div>
              <div className="mt-4">
                <EventImagePicker value={editData.image_url} onChange={(url) => setEditData(d => ({...d, image_url: url}))} />
              </div>
              <textarea value={editData.description} onChange={(e) => setEditData(d => ({...d, description: e.target.value}))} placeholder="Beschreibung..." className="w-full h-16 mt-4 px-3 py-2 bg-[#141413] border border-amber-400/30 rounded-lg text-stone-200 text-sm font-body resize-none focus:outline-none focus:ring-1 focus:ring-amber-400/50" />
              <div className="mt-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Switch checked={editData.published} onCheckedChange={(v) => setEditData(d => ({...d, published: v}))} />
                  <span className="text-stone-400 text-sm font-body">Veröffentlicht</span>
                </div>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" className="text-stone-500" onClick={() => setEditId(null)}><X size={14} className="mr-1" /> Abbrechen</Button>
                  <Button size="sm" className="bg-emerald-500 hover:bg-emerald-400 text-[#141413] text-xs" onClick={saveEdit} data-testid="event-save"><Check size={14} className="mr-1" /> Speichern</Button>
                </div>
              </div>
            </div>
          ) : (
            <div key={ev.id} className="glass-card rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0 flex-1">
                {ev.image_url ? <img src={ev.image_url} alt="" className="w-16 h-12 rounded-lg object-cover shrink-0" /> : <div className="w-16 h-12 rounded-lg bg-[#141413] border border-white/5 flex items-center justify-center shrink-0"><Image size={16} className="text-stone-700" /></div>}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-stone-200 font-body font-medium text-sm truncate">{ev.title}</p>
                    {ev.event_type && <Badge className="bg-amber-500/10 text-amber-400 border-0 text-[9px] shrink-0">{ev.event_type}</Badge>}
                    <Badge className={`border-0 text-[9px] ${ev.published ? 'bg-emerald-500/10 text-emerald-400' : 'bg-stone-500/10 text-stone-500'}`}>{ev.published ? 'Live' : 'Entwurf'}</Badge>
                  </div>
                  <p className="text-stone-600 text-[10px] font-body">{ev.date} {ev.time && `· ${ev.time}`}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Switch checked={ev.published} onCheckedChange={() => toggle(ev)} />
                <Button variant="ghost" size="icon" className="h-7 w-7 text-stone-500 hover:text-amber-400" onClick={() => startEdit(ev)} data-testid={`event-edit-${ev.id}`}><Pencil size={12} /></Button>
                <Button variant="ghost" size="icon" className="h-7 w-7 text-stone-500 hover:text-red-400" onClick={() => remove(ev)}><Trash2 size={13} /></Button>
              </div>
            </div>
          )
        ))}
      </div>
    </div>
  );
}

/* ════════════ IMAGES ════════════ */
function ImageCard({ img, onReplace, onToggle, onRemove, subtitle }) {
  const [uploading, setUploading] = useState(false);
  const hasImage = Boolean(img.image_url);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !onReplace) return;
    setUploading(true);
    try { await onReplace(file); } finally { setUploading(false); }
  };

  return (
    <div className="glass-card rounded-xl overflow-hidden">
      {/* Image Preview */}
      <div className="h-40 sm:h-44 relative bg-[#1C1C1B]">
        {hasImage ? (
          <img src={img.image_url} alt={img.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center">
            <Image size={32} className="text-stone-700 mb-2" />
            <p className="text-stone-600 text-xs font-body">Kein Bild</p>
          </div>
        )}
        {uploading && (
          <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
            <div className="flex flex-col items-center gap-2">
              <Loader2 size={24} className="text-amber-400 animate-spin" />
              <span className="text-white text-xs font-body">Wird hochgeladen...</span>
            </div>
          </div>
        )}
        {onToggle && (
          <div className="absolute top-2 left-2">
            <Badge className={`border-0 text-[9px] ${img.active !== false ? 'bg-emerald-500/10 text-emerald-400' : 'bg-stone-500/10 text-stone-500'}`}>
              {img.active !== false ? 'Live' : 'Aus'}
            </Badge>
          </div>
        )}
      </div>

      {/* Info + Actions */}
      <div className="p-4">
        <p className="text-stone-200 text-sm font-body font-semibold truncate">{img.name}</p>
        {subtitle && <p className="text-stone-500 text-[10px] font-body mt-0.5">{subtitle}</p>}

        {/* Upload Button - always visible, large, mobile-friendly */}
        <label className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-amber-500/10 hover:bg-amber-500/20 border border-amber-400/20 hover:border-amber-400/40 rounded-lg cursor-pointer transition-all active:scale-[0.98]">
          <Upload size={15} className="text-amber-400" />
          <span className="text-amber-400 text-sm font-body font-medium">
            {hasImage ? 'Foto ändern' : 'Foto hochladen'}
          </span>
          <input type="file" accept="image/*" className="hidden" onChange={handleFile} disabled={uploading} />
        </label>

        {/* Secondary actions row */}
        {(onToggle || onRemove) && (
          <div className="flex items-center justify-between mt-2.5 pt-2.5 border-t border-white/5">
            {onToggle ? (
              <div className="flex items-center gap-2">
                <Switch checked={img.active !== false} onCheckedChange={onToggle} />
                <span className="text-stone-500 text-[10px] font-body">{img.active !== false ? 'Sichtbar' : 'Ausgeblendet'}</span>
              </div>
            ) : <div />}
            {onRemove && (
              <Button variant="ghost" size="sm" className="h-7 text-stone-500 hover:text-red-400 text-[10px] px-2" onClick={onRemove}>
                <Trash2 size={12} className="mr-1" /> Entfernen
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function SectionHeader({ title, count, hint }) {
  return (
    <div className="mb-4">
      <h4 className="text-stone-300 text-xs font-body font-semibold uppercase tracking-[0.15em] flex items-center gap-2">
        <span className="w-5 h-px bg-amber-400/40" />{title}
        <Badge variant="outline" className="border-white/10 text-stone-600 text-[9px] ml-1">{count}</Badge>
      </h4>
      {hint && <p className="text-stone-600 text-[10px] font-body mt-1 ml-7">{hint}</p>}
    </div>
  );
}

function BilderTab({ toast }) {
  const [siteImages, setSiteImages] = useState([]);
  const [menuCats, setMenuCats] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [imgRes, catRes, evRes] = await Promise.all([
        imagesApi.getAll(), menuApi.getCategories(), eventsApi.getAll()
      ]);
      setSiteImages(imgRes.data.images);
      setMenuCats(catRes.data.categories);
      setEvents(evRes.data.events);
    } catch (e) {
      toast('Bilder-Daten konnten nicht geladen werden', 'error');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  // Upload helper for site_images
  const uploadSiteImage = async (file, section, existingImg) => {
    try {
      const res = await imagesApi.upload(file);
      if (existingImg) {
        await imagesApi.update(existingImg.id, { ...existingImg, image_url: res.data.url });
      } else {
        await imagesApi.create({ section, name: file.name, image_url: res.data.url, active: true, order: siteImages.length });
      }
      toast('Bild aktualisiert');
      load();
    } catch { toast('Upload fehlgeschlagen', 'error'); }
  };

  // Upload helper for menu category image
  const uploadMenuImage = async (file, cat) => {
    try {
      const res = await imagesApi.upload(file);
      await menuApi.updateCategory(cat.id, { ...cat, image_url: res.data.url });
      toast(`Menü-Bild "${cat.name}" aktualisiert`);
      load();
    } catch { toast('Upload fehlgeschlagen', 'error'); }
  };

  // Upload helper for event image
  const uploadEventImage = async (file, ev) => {
    try {
      const res = await imagesApi.upload(file);
      await eventsApi.update(ev.id, { ...ev, image_url: res.data.url });
      toast(`Event-Bild "${ev.title}" aktualisiert`);
      load();
    } catch { toast('Upload fehlgeschlagen', 'error'); }
  };

  const toggleSiteImage = async (img) => {
    await imagesApi.update(img.id, { ...img, active: !img.active });
    toast(img.active ? 'Ausgeblendet' : 'Sichtbar');
    load();
  };

  const removeSiteImage = async (img) => {
    await imagesApi.delete(img.id);
    toast('Bild entfernt');
    load();
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="text-amber-400 animate-spin" size={32} /></div>;

  const grouped = {};
  siteImages.forEach((img) => { (grouped[img.section] = grouped[img.section] || []).push(img); });

  const sectionOrder = ['hero', 'gallery', 'entertainment'];
  const sectionLabels = { hero: 'Hero', gallery: 'Galerie', entertainment: 'Spiel & Unterhaltung' };
  const sectionHints = {
    hero: 'Hauptbild auf der Startseite',
    gallery: 'Wird im Bereich Galerie dargestellt',
    entertainment: 'Wird im Freizeitbereich angezeigt',
  };

  return (
    <div>
      <h3 className="text-stone-100 font-display font-bold text-lg tracking-wide uppercase mb-2">Bildverwaltung</h3>
      <p className="text-stone-500 text-sm font-body mb-8">Alle Bilder der Website zentral verwalten. Änderungen erscheinen sofort auf der öffentlichen Seite.</p>

      {/* ── Site Images (Hero, Gallery, Entertainment) ── */}
      {sectionOrder.map((section) => {
        const imgs = grouped[section] || [];
        if (imgs.length === 0) return null;
        return (
          <div key={section} className="mb-10">
            <SectionHeader title={sectionLabels[section] || section} count={imgs.length} hint={sectionHints[section]} />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {imgs.map((img) => (
                <ImageCard key={img.id} img={img} subtitle={sectionLabels[section]}
                  onReplace={(file) => uploadSiteImage(file, section, img)}
                  onToggle={() => toggleSiteImage(img)}
                  onRemove={() => removeSiteImage(img)} />
              ))}
              <label className="glass-card rounded-xl border-dashed border-2 border-white/10 flex flex-col items-center justify-center min-h-[260px] cursor-pointer hover:border-amber-400/30 transition-colors group active:scale-[0.98]">
                <div className="w-14 h-14 rounded-full bg-amber-500/10 flex items-center justify-center mb-3 group-hover:bg-amber-500/20 transition-colors">
                  <Plus size={24} className="text-amber-400" />
                </div>
                <p className="text-stone-300 text-sm font-body font-medium">Neues Bild hochladen</p>
                <p className="text-stone-600 text-[10px] font-body mt-1">JPG, PNG, WebP bis 5 MB</p>
                <input type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadSiteImage(f, section, null); }} />
              </label>
            </div>
          </div>
        );
      })}

      {/* ── Menü-Bilder ── */}
      <div className="mb-10">
        <SectionHeader title="Menü-Bilder" count={menuCats.length} hint="Sichtbar im öffentlichen Menü bei Kategorie-Wechsel (linkes Bild)" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {menuCats.map((cat) => (
            <ImageCard key={cat.id}
              img={{ name: cat.name, image_url: cat.image_url, active: cat.active }}
              subtitle={`Menü-Kategorie · ${cat.item_count || 0} Positionen`}
              onReplace={(file) => uploadMenuImage(file, cat)}
              onToggle={null}
              onRemove={null} />
          ))}
        </div>
      </div>

      {/* ── Event-Bilder ── */}
      <div className="mb-10">
        <SectionHeader title="Event-Bilder" count={events.length} hint="Erscheint im Bereich Events auf der Website" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {events.map((ev) => (
            <ImageCard key={ev.id}
              img={{ name: ev.title, image_url: ev.image_url, active: ev.published }}
              subtitle={`${ev.event_type || 'Event'} · ${ev.published ? 'Live' : 'Entwurf'}`}
              onReplace={(file) => uploadEventImage(file, ev)}
              onToggle={null}
              onRemove={null} />
          ))}
        </div>
      </div>
    </div>
  );
}

/* ════════════ CONTENT ════════════ */
function InhalteTab({ toast }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    contentApi.getAll()
      .then((res) => setItems(res.data.content))
      .catch(() => toast('Inhalte konnten nicht geladen werden', 'error'))
      .finally(() => setLoading(false));
  }, [toast]);

  const save = async (item) => {
    await contentApi.update(item.id, item.value);
    toast(`"${item.field}" gespeichert`);
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="text-amber-400 animate-spin" size={32} /></div>;

  return (
    <div>
      <h3 className="text-stone-100 font-display font-bold text-lg tracking-wide uppercase mb-5">Inhalte & Texte</h3>
      <div className="space-y-3">
        {items.map((c) => (
          <div key={c.id} className="glass-card rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="sm:w-28 shrink-0">
              <Badge variant="outline" className="border-white/10 text-stone-500 text-[10px]">{c.section}</Badge>
              <p className="text-stone-400 text-xs font-body mt-1">{c.field}</p>
            </div>
            <div className="flex-1">
              {c.value.length > 60 ? (
                <textarea value={c.value} onChange={(e) => setItems(its => its.map(i => i.id === c.id ? {...i, value: e.target.value} : i))}
                  className="w-full h-16 px-3 py-2 bg-[#141413] border border-white/10 rounded-lg text-stone-200 text-sm font-body resize-none focus:outline-none focus:ring-1 focus:ring-amber-400/50" />
              ) : (
                <Input value={c.value} onChange={(e) => setItems(its => its.map(i => i.id === c.id ? {...i, value: e.target.value} : i))}
                  className="bg-[#141413] border-white/10 text-stone-200 text-sm" />
              )}
            </div>
            <Button variant="ghost" size="icon" className="h-8 w-8 text-stone-500 hover:text-emerald-400 shrink-0" onClick={() => save(c)}><Save size={14} /></Button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ════════════ CONTACT ════════════ */
function KontaktTab({ toast }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    contactApi.get()
      .then((res) => setData(res.data))
      .catch(() => toast('Kontaktdaten konnten nicht geladen werden', 'error'))
      .finally(() => setLoading(false));
  }, [toast]);

  const save = async () => {
    await contactApi.update(data);
    toast('Kontaktdaten gespeichert');
  };

  const setField = (key, val) => setData(d => ({...d, [key]: val}));
  const setHour = (idx, key, val) => setData(d => ({...d, hours: d.hours.map((h, i) => i === idx ? {...h, [key]: val} : h)}));

  if (loading || !data) return <div className="flex justify-center py-20"><Loader2 className="text-amber-400 animate-spin" size={32} /></div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h3 className="text-stone-100 font-display font-bold text-lg tracking-wide uppercase">Kontakt & Basisdaten</h3>
        <Button size="sm" className="bg-amber-500 hover:bg-amber-400 text-[#141413] text-xs" onClick={save}><Save size={14} className="mr-1.5" /> Speichern</Button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-4">
          {[
            { icon: MapPin, label: 'Adresse', fields: [['address_line1', 'Strasse'], ['address_line2', 'PLZ / Ort']] },
            { icon: Phone, label: 'Telefon', fields: [['phone', 'Telefonnummer']] },
            { icon: Mail, label: 'E-Mail', fields: [['email', 'E-Mail-Adresse']] },
            { icon: Globe, label: 'Social Media', fields: [['instagram', 'Instagram URL'], ['facebook', 'Facebook URL']] },
          ].map((block) => (
            <div key={block.label} className="glass-card rounded-xl p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-full bg-amber-500/10 flex items-center justify-center"><block.icon size={16} className="text-amber-400" /></div>
                <h4 className="text-stone-100 font-body font-semibold text-sm">{block.label}</h4>
              </div>
              <div className="space-y-2">
                {block.fields.map(([key, placeholder]) => (
                  <Input key={key} value={data[key] || ''} onChange={(e) => setField(key, e.target.value)} placeholder={placeholder} className="bg-[#141413] border-white/10 text-stone-200 text-sm" />
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="glass-card rounded-xl p-5">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-9 h-9 rounded-full bg-amber-500/10 flex items-center justify-center"><Clock size={16} className="text-amber-400" /></div>
            <h4 className="text-stone-100 font-body font-semibold text-sm">Öffnungszeiten</h4>
          </div>
          <div className="space-y-2.5">
            {(data.hours || []).map((d, idx) => (
              <div key={d.day} className="flex items-center gap-2">
                <span className="text-stone-300 text-sm font-body w-24 shrink-0">{d.day}</span>
                <Input value={d.open} onChange={(e) => setHour(idx, 'open', e.target.value)} placeholder="–" className="bg-[#141413] border-white/10 text-stone-200 text-sm w-[72px] text-center h-8" disabled={!d.active} />
                <span className="text-stone-600 text-xs">–</span>
                <Input value={d.close} onChange={(e) => setHour(idx, 'close', e.target.value)} placeholder="–" className="bg-[#141413] border-white/10 text-stone-200 text-sm w-[72px] text-center h-8" disabled={!d.active} />
                <Switch checked={d.active} onCheckedChange={(v) => setHour(idx, 'active', v)} />
                {!d.active && <span className="text-red-400/60 text-[10px] font-body">geschlossen</span>}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ════════════ SECURITY ════════════ */
function SicherheitTab({ toast }) {
  const [curPw, setCurPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [saving, setSaving] = useState(false);

  const changePw = async () => {
    if (newPw.length < 8) return toast('Mindestens 8 Zeichen erforderlich', 'error');
    if (newPw !== confirmPw) return toast('Passwörter stimmen nicht überein', 'error');
    setSaving(true);
    try {
      await authApi.changePassword(curPw, newPw);
      toast('Passwort erfolgreich geändert');
      setCurPw(''); setNewPw(''); setConfirmPw('');
    } catch (err) {
      toast(err.response?.data?.detail || 'Fehler beim Ändern', 'error');
    } finally { setSaving(false); }
  };

  return (
    <div>
      <h3 className="text-stone-100 font-display font-bold text-lg tracking-wide uppercase mb-5">Sicherheit</h3>
      <div className="glass-card rounded-xl p-6 max-w-lg">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-full bg-amber-500/10 flex items-center justify-center"><Shield size={18} className="text-amber-400" /></div>
          <div>
            <h4 className="text-stone-100 font-body font-semibold text-sm">Admin-Passwort ändern</h4>
            <p className="text-stone-500 text-xs font-body">Mindestens 8 Zeichen, sicher und einzigartig</p>
          </div>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-stone-400 text-[10px] font-body uppercase tracking-wider mb-1 block">Aktuelles Passwort</label>
            <Input type="password" value={curPw} onChange={(e) => setCurPw(e.target.value)} placeholder="Aktuelles Passwort" className="bg-[#141413] border-white/10 text-stone-200 text-sm" />
          </div>
          <div>
            <label className="text-stone-400 text-[10px] font-body uppercase tracking-wider mb-1 block">Neues Passwort</label>
            <Input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="Neues Passwort (min. 8 Zeichen)" className="bg-[#141413] border-white/10 text-stone-200 text-sm" />
          </div>
          <div>
            <label className="text-stone-400 text-[10px] font-body uppercase tracking-wider mb-1 block">Passwort bestätigen</label>
            <Input type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} placeholder="Neues Passwort wiederholen" className="bg-[#141413] border-white/10 text-stone-200 text-sm" />
          </div>
          <Button className="bg-amber-500 hover:bg-amber-400 text-[#141413] text-xs w-full mt-2" onClick={changePw} disabled={saving}>
            {saving ? <Loader2 size={14} className="animate-spin mr-2" /> : <Save size={14} className="mr-2" />} Passwort ändern
          </Button>
        </div>
      </div>
      <div className="glass-card rounded-xl p-6 max-w-lg mt-6">
        <h4 className="text-stone-100 font-body font-semibold text-sm mb-3">Sicherheitsstatus</h4>
        <div className="space-y-2">
          {[
            'Login-Authentifizierung aktiv',
            'Passwort sicher verschlüsselt',
            'Login-Schutz: max. 5 Versuche / 5 Min.',
            'Bild-Upload: max. 5 MB, nur Bildformate',
          ].map((label) => (
            <div key={label} className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-emerald-400" />
              <span className="text-stone-400 text-sm font-body">{label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ════════════ MAIN ════════════ */
export default function AdminPage() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('dashboard');
  const { toasts, show } = useToast();
  const [stats, setStats] = useState([]);

  useEffect(() => {
    Promise.all([menuApi.getCategories(), eventsApi.getAll(), imagesApi.getAll()]).then(([m, e, i]) => {
      const totalItems = m.data.categories.reduce((s, c) => s + (c.item_count || 0), 0);
      setStats([
        { label: 'Kategorien', value: String(m.data.categories.length) },
        { label: 'Menü-Positionen', value: String(totalItems) },
        { label: 'Events', value: String(e.data.events.length) },
        { label: 'Bilder', value: String(i.data.images.length) },
      ]);
    }).catch(() => {});
  }, [tab]);

  const handleLogout = async () => { await logout(); navigate('/login'); };

  const tabMap = {
    dashboard: <DashboardTab setTab={setTab} stats={stats} />,
    menu: <MenuTab toast={show} />,
    events: <EventsTab toast={show} />,
    bilder: <BilderTab toast={show} />,
    inhalte: <InhalteTab toast={show} />,
    kontakt: <KontaktTab toast={show} />,
    sicherheit: <SicherheitTab toast={show} />,
  };

  return (
    <div data-testid="admin-page" className="min-h-screen bg-[#141413]">
      <ToastContainer toasts={toasts} />
      <div className="border-b border-white/5 bg-[#141413]/90 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-[1440px] mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a href="/" data-testid="admin-back-link" className="text-stone-500 hover:text-stone-300 transition-colors"><ArrowLeft size={18} /></a>
            <Separator orientation="vertical" className="h-5 bg-white/10" />
            <img src={LOGO_URL} alt="TreffPunkt" className="h-8 w-auto object-contain" />
            <span className="text-[9px] tracking-[0.2em] text-stone-600 uppercase hidden sm:inline">Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <a href="/" target="_blank" className="text-stone-500 hover:text-amber-400 transition-colors flex items-center gap-1.5 text-xs font-body"><ExternalLink size={13} /> Website</a>
            <button onClick={handleLogout} data-testid="admin-logout" className="text-stone-500 hover:text-red-400 transition-colors flex items-center gap-1.5 text-xs font-body"><LogOut size={13} /> Abmelden</button>
          </div>
        </div>
      </div>
      <div className="max-w-[1440px] mx-auto px-6 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          <nav className="lg:w-48 shrink-0 flex lg:flex-col gap-1.5 overflow-x-auto lg:overflow-x-visible pb-2 lg:pb-0 lg:sticky lg:top-[72px] lg:self-start">
            {TABS.map((t) => (
              <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
                className={`flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-body font-medium transition-all whitespace-nowrap ${tab === t.id ? 'bg-amber-500/10 text-amber-400 border border-amber-400/20' : 'text-stone-500 hover:text-stone-300 hover:bg-white/[0.03]'}`}>
                <t.icon size={16} />{t.label}{tab === t.id && <ChevronRight size={13} className="ml-auto hidden lg:block" />}
              </button>
            ))}
          </nav>
          <main className="flex-1 min-w-0">{tabMap[tab]}</main>
        </div>
      </div>
    </div>
  );
}
